import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';

import {
  Flag,
  IconWrapper,
  StyledNationalitySelect,
  StyledNationalitySelectLabel,
  StyledSelect,
  Wrapper,
} from './styles';
import { findCountryByAlpha3Code, getSortedCountries } from '@src/common/auth';

type NationalitySelectProps = {
  label?: string;
  name?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  showFlag?: boolean;
  disabled?: boolean;
};

const NationalitySelect = forwardRef<HTMLSelectElement, NationalitySelectProps>(
  (props: NationalitySelectProps, inputRef: React.Ref<HTMLSelectElement>) => {
    const { label, name, value, onChange, showFlag = true, disabled } = props;

    const [internalValue, setInternalValue] = useState<string | undefined>(value);

    const selectedCountry = useMemo(() => findCountryByAlpha3Code(internalValue ?? ''), [internalValue]);
    const sortedCountries = useMemo(() => getSortedCountries(), []);

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setInternalValue(e.target.value);

      if (onChange) {
        onChange(e);
      }
    };

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    return (
      <Wrapper>
        {label ? <StyledNationalitySelectLabel>{label}</StyledNationalitySelectLabel> : null}

        <StyledSelect>
          {showFlag ? (
            <Flag>
              <img src={selectedCountry?.image} alt={selectedCountry?.name} />
            </Flag>
          ) : null}

          <StyledNationalitySelect
            name={name}
            defaultValue=''
            value={internalValue}
            onChange={handleChange}
            ref={inputRef}
            disabled={disabled}
          >
            <option key={`country-undefined`} value='' disabled></option>

            {sortedCountries.map((country) => (
              <option key={`country-${country.alpha3}`} value={country.alpha3}>
                {country.name}
              </option>
            ))}
          </StyledNationalitySelect>

          <IconWrapper>
            <FiChevronDown size={24} />
          </IconWrapper>
        </StyledSelect>
      </Wrapper>
    );
  },
);

NationalitySelect.displayName = 'NationalitySelect';

export default NationalitySelect;
